import { useLingui } from '@lingui/react/macro';
import { type CategoryKey } from '@worldcoo/shared/core';

/**
 * The order the eight action-scope categories are presented in, everywhere they are listed: the NGO
 * onboarding and settings pickers, and the public "ONG colaboradoras" directory rows.
 *
 * NOT `categoriesKeys` order — that array is positional (its index IS the stored category number, so
 * it can never be resorted). This is the editorial order the Figma hand-off draws, health first.
 */
export const CATEGORY_ORDER = [
	'health',
	'environment',
	'education',
	'habitat',
	'feeding',
	'humanRights',
	'emergency',
	'basicNeeds',
] as const satisfies readonly CategoryKey[];

/**
 * Translated label per category key.
 *
 * One source for all of them: the same eight strings were previously typed out in the NGO onboarding
 * and settings forms, which is how `habitat` came to read "Social Inclusion" in one place and risked
 * drifting in the next. A `Record` rather than an options array — callers that need `{value,label}`
 * pairs map `CATEGORY_ORDER` over it, and callers that need to label a single key just index it.
 */
export function useCategoryLabels(): Record<CategoryKey, string> {
	const { t } = useLingui();
	return {
		health: t`Health`,
		environment: t`Environment`,
		education: t`Education & Job Insertion`,
		habitat: t`Social Inclusion`,
		feeding: t`Hunger & Food Security`,
		humanRights: t`Human Rights`,
		emergency: t`Emergencies`,
		basicNeeds: t`Water & Energy`,
	};
}

/** `CATEGORY_ORDER` as the `{ value, label }` shape the form pickers feed to their selects. */
export function useCategoryOptions(): { value: CategoryKey; label: string }[] {
	const labels = useCategoryLabels();
	return CATEGORY_ORDER.map((value) => ({ value, label: labels[value] }));
}
